import axios from 'axios'

export const readPhase3Text = (value) => {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number') return value.toString().trim()
  if (typeof value === 'object') return (value.name || value.title || value.label || '').toString().trim()
  return ''
}

export const sanitizePhase3MoneyInput = (value) => value == null ? '' : value.toString().replace(/\D/g, '')

const parsePhase3MoneyValue = (value) => {
  if (value == null) return null
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : null

  const rawValue = value.toString().trim().replace(/[^\d.,-]/g, '')
  if (!rawValue) return null

  const lastDot = rawValue.lastIndexOf('.')
  const lastComma = rawValue.lastIndexOf(',')
  let normalizedValue = rawValue

  if (lastDot >= 0 && lastComma >= 0) {
    const decimalSeparator = lastDot > lastComma ? '.' : ','
    const thousandsSeparator = decimalSeparator === '.' ? ',' : '.'
    normalizedValue = rawValue
      .replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '')
      .replace(decimalSeparator, '.')
  }
  else {
    const separator = lastDot >= 0 ? '.' : (lastComma >= 0 ? ',' : null)

    if (separator) {
      const separatorPattern = new RegExp(`\\${separator}`, 'g')
      const parts = rawValue.split(separator)
      const decimalDigits = parts[parts.length - 1]

      normalizedValue = decimalDigits.length <= 2
        ? `${parts.slice(0, -1).join('')}.${decimalDigits}`
        : rawValue.replace(separatorPattern, '')
    }
  }

  const numericValue = Number(normalizedValue)
  return Number.isFinite(numericValue) ? Math.trunc(numericValue) : null
}

export const formatPhase3MoneyInput = (value) => {
  const numericValue = parsePhase3MoneyValue(value)
  return numericValue == null ? '' : numericValue.toLocaleString('id-ID')
}

export const formatPhase3Money = (value) => {
  const number = parseInt(value || 0, 10)
  if (Number.isNaN(number) || number <= 0) return '-'
  return `IDR ${number.toLocaleString('en-US')}`
}

export const formatPhase3PriceTagMoney = (value) => {
  const number = parseInt(value || 0, 10)
  if (Number.isNaN(number) || number <= 0) return 'N/A'
  return `IDR ${number.toLocaleString('en-US')},-`
}

export const formatPhase3Date = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('id-ID', {
    day    : '2-digit',
    month  : 'short',
    year   : 'numeric',
    hour   : '2-digit',
    minute : '2-digit'
  })
}

export const formatPhase3InvoiceDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-GB', {
    day   : 'numeric',
    month : 'long',
    year  : '2-digit'
  })
}

export const normalizePhase3ImageUrl = (urlOrName) => {
  if (!urlOrName) return ''
  if (urlOrName.startsWith('http')) return urlOrName
  if (urlOrName.startsWith('data:')) return urlOrName
  if (urlOrName.startsWith('/')) return `${axios.defaults.baseURL || ''}${urlOrName}`
  return `http://localhost:8000/${encodeURIComponent(urlOrName)}`
}

export const getPhase3ErrorMessage = (error) => {
  const data = error?.response?.data
  if (!data) return error?.message || 'Unknown error'
  if (typeof data === 'string') return data
  if (data.message) return data.message
  if (data.error) return data.error
  if (data.messages?.base) {
    const missing = data.messages?.missing_fields
    if (Array.isArray(missing) && missing.length > 0) {
      return `${data.messages.base}: ${missing.join(', ')}`
    }
    return data.messages.base
  }
  if (Array.isArray(data.errors)) return data.errors.join(', ')
  return JSON.stringify(data)
}

export const extractPhase3Products = (response) => {
  const data = response?.data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.products)) return data.products
  if (Array.isArray(data)) return data
  return []
}

export const extractPhase3Product = (response) => {
  const data = response?.data
  return data?.data || data?.product || data || null
}

export const extractPhase3Meta = (response) => {
  return response?.data?.meta || {}
}

export const extractPhase3Deals = (response) => {
  const data = response?.data
  if (Array.isArray(data?.deals)) return data.deals
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data)) return data
  return []
}

export const extractPhase3Deal = (response) => {
  const data = response?.data
  return data?.deal || data?.data || data || null
}

export const getPhase3CartItems = (deal) => {
  if (Array.isArray(deal?.cart?.cart_items)) return deal.cart.cart_items
  if (Array.isArray(deal?.cart_items)) return deal.cart_items
  if (Array.isArray(deal?.items)) return deal.items
  return []
}

export const getPhase3UserName = (user) => {
  return readPhase3Text(user?.detail?.formatted_name) ||
    readPhase3Text(user?.detail?.name) ||
    readPhase3Text(user?.name) ||
    readPhase3Text(user?.email)
}

export const getPhase3UserAddress = (user) => {
  return readPhase3Text(user?.detail?.complete_address) ||
    readPhase3Text(user?.complete_address) ||
    readPhase3Text(user?.address)
}

export const getPhase3UserPhone = (user) => {
  const phoneCode = readPhase3Text(user?.detail?.phone_code || user?.phone_code)
  const phone = readPhase3Text(user?.detail?.phone || user?.phone)
  return [ phoneCode, phone ].filter(Boolean).join(' ')
}

export const getPhase3ProductImageUrl = (product) => {
  return product?.images?.comes_with_image_url ||
    product?.images?.main_image_url ||
    product?.comes_with_image_url ||
    product?.main_image_url ||
    ''
}

export const getPhase3StampImageUrl = (product) => {
  return product?.images?.stamp_image_url || product?.stamp_image_url || ''
}

export const getPhase3BrandName = (product = {}) => {
  const candidates = [
    product?.designer_name,
    product?.designer?.name,
    product?.brand?.name,
    product?.brand_name,
    product?.summary_index?.brand,
    product?.summary_index?.designer
  ]

  for (const candidate of candidates) {
    const value = readPhase3Text(candidate)
    if (value) return value
  }

  return ''
}

export const getPhase3ConditionName = (product = {}) => {
  const candidates = [
    product?.condition_name,
    product?.condition?.name,
    product?.summary_index?.condition
  ]

  for (const candidate of candidates) {
    const value = readPhase3Text(candidate)
    if (value) return value
  }

  return ''
}

export const isPhase3BktSku = (product = {}) => {
  const skuCandidates = [ product?.sku, product?.product_sku, product?.summary_index?.sku ]
  return skuCandidates.some((sku) => /^BKT/i.test(readPhase3Text(sku)))
}

export const stripLeadingPhase3Brand = (value = '', brand = '') => {
  const text = readPhase3Text(value)
  const brandName = readPhase3Text(brand)
  if (!text || !brandName) return text

  return text
    .replace(new RegExp(`^${brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s+|\\s*-\\s*|\\s*:\\s*)?`, 'i'), '')
    .trim()
}

export const getPhase3InvoiceTitleFromProduct = (product = {}) => {
  const isBkt = isPhase3BktSku(product)
  const brand = getPhase3BrandName(product)
  const condition = getPhase3ConditionName(product)
  const name = readPhase3Text(product?.name)
  const stamp = readPhase3Text(product?.stamp)

  const displayName = isBkt ? stripLeadingPhase3Brand(name, brand) : name
  const displayStamp = isBkt ? stripLeadingPhase3Brand(stamp, brand) : stamp

  return [
    !isBkt ? condition : '',
    !isBkt ? brand : '',
    displayName,
    displayStamp
  ].filter(Boolean).join(' ')
}

export const getPhase3InvoiceLineFromProduct = (product = {}, cartItem = {}) => {
  const unitPrice = parseInt(cartItem.price || cartItem.final_price || product.selling_price || 0, 10)

  return {
    id            : product.id,
    cartItemId    : cartItem.id,
    sku           : product.sku,
    title         : getPhase3InvoiceTitleFromProduct(product),
    description   : product.comes_with || '',
    quantity      : Math.max(1, parseInt(cartItem.quantity || 1, 10)),
    currency      : 'IDR',
    amount        : Number.isNaN(unitPrice) ? 0 : unitPrice,
    mainImageUrl  : getPhase3ProductImageUrl(product),
    stampImageUrl : getPhase3StampImageUrl(product)
  }
}

export const normalizePhase3ShoeSizeInput = (value) => {
  const rawValue = value == null ? '' : String(value).trim()

  if (!rawValue) return ''

  const normalizedValue = rawValue.replace(',', '.')

  if (!/^\d+(?:\.\d+)?$/.test(normalizedValue)) return ''

  const numericValue = Number(normalizedValue)

  if (!Number.isFinite(numericValue)) return ''

  return String(numericValue)
}

export const isPhase3ShoesProduct = (product = {}) => {
  if (Number(product.category_id) === 2) return true

  return String(product.category_name || '').trim().toLowerCase() === 'shoes'
}

export const normalizePhase3InsoleSizeInput = (value) => {
  const normalizedValue = normalizePhase3ShoeSizeInput(value)

  if (!normalizedValue) return ''

  return Number(normalizedValue) > 0
    ? normalizedValue
    : ''
}

export const buildPhase3ShoesSizingPayload = (product = {}, form = {}) => {
  if (!isPhase3ShoesProduct(product)) {
    return {
      p_size: form.p_size
    }
  }

  const rawProductSize = form.p_size == null
    ? ''
    : String(form.p_size).trim()

  const normalizedProductSize = normalizePhase3ShoeSizeInput(rawProductSize)

  const payload = {
    p_size: normalizedProductSize || rawProductSize
  }

  const rawInsoleSize = form.insole_size == null
    ? ''
    : String(form.insole_size).trim()

  if (!rawInsoleSize) {
    payload.insole_size = ''
    return payload
  }

  const normalizedInsoleSize = normalizePhase3InsoleSizeInput(rawInsoleSize)

  if (normalizedInsoleSize) {
    payload.insole_size = normalizedInsoleSize
  }

  return payload
}

export const validatePhase3ShoesSizing = (product = {}, form = {}) => {
  if (!isPhase3ShoesProduct(product)) return ''

  if (!normalizePhase3ShoeSizeInput(form.p_size)) {
    return 'Product Size wajib diisi dengan angka untuk kategori Shoes.'
  }

  if (!normalizePhase3InsoleSizeInput(form.insole_size)) {
    return 'Insole Size wajib diisi dengan angka lebih dari 0 untuk kategori Shoes.'
  }

  return ''
}

export const getPhase3InsolePhotoField = (product = {}) => {
  if (!isPhase3ShoesProduct(product)) return null

  return {
    key      : 'insole_photo',
    label    : 'Insole Photo',
    imageKey : 'insole_photo_url'
  }
}
